/**
 * RecipientApiClient — the HTTP transport layer between the recipient web app
 * and the Legacy Architect API. This is the piece the memory flagged as
 * missing: it speaks the snake_case wire contract and adapts to the shapes the
 * client crypto (`decryptMessage`) expects.
 *
 * Endpoints (all under /api, proxied to the API in dev):
 *   GET  /unlock/:token/info               → landing summary (public)
 *   POST /unlock/:token/session            → { messageId, accessToken } (public)
 *   GET  /recipient/messages/:id/decrypt-context  (Bearer accessToken)
 *   POST /onboard/public-key               → register device public key (public)
 */

export interface UnlockInfo {
  sender_name: string | null;
  sealed_at: string | null;
  recipient_name?: string | null;
  status?: string;
}

export interface SessionResult {
  messageId: string;
  accessToken: string;
  expiresInSeconds: number;
  firstView: boolean;
  /** Durable "remember-this-device" token — persist to return without redoing the unlock. */
  durableToken: string;
  durableExpiresInSeconds: number;
}

/** One delivered message in the recipient's permanent library (GET /api/recipient/messages). */
export interface RecipientInboxItem {
  id: string;
  format: 'video' | 'voice' | 'letter' | 'document';
  title: string | null;
  sender_name: string;
  delivered_at: string;
  first_played_at: string | null;
  watched_count: number;
}

/** GET /api/recipient/profile — the recipient's own reachability record (E6-S8). */
export interface RecipientProfileWire {
  recipient_id: string;
  vault_id: string;
  full_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  contact_methods: Array<{ type: string; value: string; verified: boolean }>;
  verified_method_count: number;
}

/** POST /api/recipients/welcome/start — the invite link's first step. */
export interface WelcomeStart {
  kind: 'unlock' | 'pending_invite';
  recipient_first_name: string;
  expires_in_seconds: number;
  user_exists: boolean;
}

/**
 * POST /api/recipients/welcome/link — the step that closes the delivery loop.
 *
 * A proven code does two things at once: it flips the recipient's email contact
 * to verified (the single fact `delivery-email.worker` requires before it will
 * send — without it a message sealed for a living adult never arrives), and it
 * returns an `onboard_ticket` authorizing exactly one device-key registration
 * for `email`. The ticket is bound to the address the VAULT OWNER recorded, not
 * to anything the browser typed, so one code can safely do both.
 */
export interface WelcomeLink {
  recipient_id: string;
  email_contact_verified: boolean;
  user_id: string | null;
  /** Present only when a code was proven; hold in memory, never persist. */
  onboard_ticket: string | null;
  /** The recipient email resolved from the invite token. */
  email: string;
  /** FALSE when this person already has a device key registered. */
  needs_key: boolean;
  /**
   * users.id for that email, if one exists — distinct from `user_id`, which is
   * the user this recipient row is already linked to (null before linkage).
   * Needed to bind `recipients.user_id` for someone who already has a key: that
   * linkage is how the seal path finds a recipient's public key at all.
   */
  existing_user_id: string | null;
}

/** Raw decrypt-context shape `decryptMessage` consumes (snake_case wire). */
export interface DecryptContextWire {
  message_id: string;
  cek_ciphertext: string;
  format: 'video' | 'voice' | 'letter' | 'document';
  iv_b64: string;
  download_url: string | null;
  letter_ciphertext_b64: string | null;
  mime_type: string;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class RecipientApiClient {
  constructor(private readonly baseUrl = '') {}

  async unlockInfo(token: string): Promise<UnlockInfo> {
    return this.json('GET', `/api/unlock/${encodeURIComponent(token)}/info`);
  }

  /** Verify token + OTP and obtain a recipient session token. */
  async session(token: string, otpCode: string): Promise<SessionResult> {
    return this.json('POST', `/api/unlock/${encodeURIComponent(token)}/session`, { otp_code: otpCode });
  }

  /**
   * Welcome step 1: the recipient opened the invite link. Sends a 6-digit code
   * to the email the vault owner recorded for them (and their phone, if one is
   * on file). The address is never disclosed to the caller — the token resolves
   * it server-side.
   */
  async welcomeStart(token: string): Promise<WelcomeStart> {
    return this.json('POST', '/api/recipients/welcome/start', { token });
  }

  /**
   * Welcome step 2: prove the code. Flips the email contact to verified and
   * returns a single-use onboard ticket for the device-key registration.
   *
   * Called a second time with `{ token, userId }` after the key is registered,
   * to link the account and consume the invite.
   */
  async welcomeLink(
    token: string,
    body: { code: string } | { userId: string; onboardTicket: string },
  ): Promise<WelcomeLink> {
    return this.json('POST', '/api/recipients/welcome/link', { token, ...body });
  }

  /** Onboarding step 1: email a 6-digit code proving the recipient controls this inbox. */
  async onboardOtpSend(email: string): Promise<{ otpId: string; expiresInSeconds: number }> {
    return this.json('POST', '/api/onboard/otp/send', { email });
  }

  /**
   * Onboarding step 2: verify the code. Returns a short-lived, single-use
   * `onboardTicket` — a bearer credential that authorizes exactly one key
   * registration for this email. Hold it in memory only; never persist it.
   */
  async onboardOtpVerify(email: string, code: string): Promise<{ verified: boolean; onboardTicket?: string }> {
    return this.json('POST', '/api/onboard/otp/verify', { email, code });
  }

  /**
   * Onboarding step 3: register the device public key against the recipient
   * email. Requires the `onboardTicket` from `onboardOtpVerify` — without a
   * fresh proof of email control the server refuses to write the key (a
   * stranger who merely knew the email could otherwise substitute their own).
   */
  async registerPublicKey(email: string, publicKey: string, onboardTicket: string): Promise<{ ok: boolean; userId: string }> {
    return this.json('POST', '/api/onboard/public-key', { email, public_key: publicKey }, onboardTicket);
  }

  /** Trade a durable "remember-this-device" token for a fresh short-lived access token. */
  async refreshSession(durableToken: string): Promise<{ accessToken: string; expiresInSeconds: number }> {
    return this.json('POST', '/api/recipient/session/refresh', { durable_token: durableToken });
  }

  /** Sign this device out — revoke the durable token server-side. */
  async revokeSession(durableToken: string): Promise<{ ok: boolean }> {
    return this.json('POST', '/api/recipient/session/revoke', { durable_token: durableToken });
  }

  /** The recipient's permanent library — every delivered message (Bearer accessToken). */
  async listInbox(accessToken: string): Promise<{ messages: RecipientInboxItem[] }> {
    return this.json('GET', '/api/recipient/messages', undefined, accessToken);
  }

  /**
   * Record a playback event (E6-S4) — a delivery/read receipt for the owner.
   * The first 'started' marks the message truly delivered (first_played_at).
   * Best-effort: a failure here never disrupts playback, so callers ignore it.
   */
  async playbackEvent(
    accessToken: string,
    messageId: string,
    event: 'started' | 'completed' | 'paused',
    extra?: { positionSeconds?: number; durationSeconds?: number },
  ): Promise<void> {
    await this.json(
      'POST',
      `/api/recipient/messages/${encodeURIComponent(messageId)}/playback-event`,
      {
        event,
        position_seconds: extra?.positionSeconds ?? null,
        duration_seconds: extra?.durationSeconds ?? null,
      },
      accessToken,
    );
  }

  /** The recipient's own "how we reach you" profile (E6-S8, Bearer accessToken). */
  async getProfile(accessToken: string): Promise<RecipientProfileWire> {
    return this.json('GET', '/api/recipient/profile', undefined, accessToken);
  }

  /** Context to seal a reply — the vault owner's current key (Bearer accessToken). */
  async responseContext(
    sourceMessageId: string,
    accessToken: string,
  ): Promise<{ source_message_id: string; vault_owner_public_key: string; cek_algorithm_version: string }> {
    return this.json(
      'GET',
      `/api/recipient/messages/${encodeURIComponent(sourceMessageId)}/response-context`,
      undefined,
      accessToken,
    );
  }

  /** Seal a reply back to the vault owner (Bearer accessToken). Maps camelCase → wire. */
  async postResponse(
    input: {
      sourceMessageId: string;
      format: 'video' | 'voice' | 'letter';
      cekCiphertext: string;
      ownerPublicKey: string;
      cekAlgorithmVersion: string;
      ivB64: string;
      r2Key: string | null;
      letterCiphertextB64: string | null;
    },
    accessToken: string,
  ): Promise<{ response_id: string; sealed_at: string }> {
    return this.json(
      'POST',
      `/api/messages/${encodeURIComponent(input.sourceMessageId)}/responses`,
      {
        format: input.format,
        cek_ciphertext: input.cekCiphertext,
        owner_public_key: input.ownerPublicKey,
        cek_algorithm_version: input.cekAlgorithmVersion,
        iv_b64: input.ivB64,
        r2_key: input.r2Key,
        letter_ciphertext_b64: input.letterCiphertextB64,
      },
      accessToken,
    );
  }

  /** Build a DecryptApiClient (the interface decryptMessage expects) bound to a session. */
  decryptApi(accessToken: string) {
    return {
      fetchDecryptContext: async (messageId: string): Promise<DecryptContextWire> =>
        this.json('GET', `/api/recipient/messages/${encodeURIComponent(messageId)}/decrypt-context`, undefined, accessToken),
    };
  }

  private async json<T>(method: string, path: string, body?: unknown, bearer?: string): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    const res = await fetch(this.baseUrl + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    const parsed = text ? safeParse(text) : undefined;
    if (!res.ok) {
      const msg = (parsed && (parsed.message || parsed.error)) || res.statusText || `HTTP ${res.status}`;
      throw new ApiError(res.status, String(msg));
    }
    return parsed as T;
  }
}

function safeParse(text: string): any {
  try { return JSON.parse(text); } catch { return { message: text }; }
}
